import React, { useEffect, useMemo, useState } from 'react';
import { boreholesApi } from '../../lib/api';
import { bboxAround, boreholeCylinders, boreholeOrigin, loadColorOverrides } from '../../lib/boreholes';

const MAX_CYLINDERS = 4000;

/**
 * Boreholes in the 3D scene: one vertical cylinder per lithology interval, coloured by
 * lithology, at the hole's real position. Rendered inside SubsurfaceViewer3D's Canvas
 * through its `sceneExtras` injection point, in the same local frame as the fence and
 * voxel models (x = east, y = -depth, z = north, metres from `origin`).
 *
 * With an origin (the scene's focused line or first fence model) it loads the boreholes
 * within `radius` metres of it; without one it loads the caller's boreholes (capped) and
 * centres on them. `groundY(lon, lat, collarElevation)` (from terrain/TerrainMesh) lifts
 * each hole to its ground in the draped-terrain frame.
 */
export default function Boreholes3D({ origin = null, radius = 5000, columnRadius = 1.5, groundY = null }) {
  const [holes, setHoles] = useState([]);
  // Plain numbers as effect inputs: the parent builds a new origin object on each render.
  const lon0 = origin ? origin.lon0 : null;
  const lat0 = origin ? origin.lat0 : null;
  const hasOrigin = Number.isFinite(lon0) && Number.isFinite(lat0);

  useEffect(() => {
    let active = true;
    const params = { include_intervals: true, limit: hasOrigin ? 2000 : 500 };
    if (hasOrigin) params.bbox = bboxAround({ lon0, lat0 }, radius);
    boreholesApi.list(params)
      .then(res => { if (active) setHoles(res.data?.boreholes || []); })
      .catch(() => { if (active) setHoles([]); }); // no borehole tables yet: draw nothing
    return () => { active = false; };
  }, [hasOrigin, lon0, lat0, radius]);

  const cylinders = useMemo(() => {
    const o = hasOrigin ? { lon0, lat0 } : boreholeOrigin(holes);
    const cyl = boreholeCylinders(holes, o, loadColorOverrides()).slice(0, MAX_CYLINDERS);
    if (!groundY) return cyl;
    // DEM terrain (docs/DEM_TERRAIN.md): hang each hole from its collar elevation, else the DEM
    const offset = new Map(holes.map(b => [String(b.id), groundY(b.lon, b.lat, b.elevation)]));
    return cyl.map(c => {
      const dy = offset.get(c.key.slice(0, c.key.lastIndexOf('-'))) || 0;
      return { ...c, position: [c.position[0], c.position[1] + dy, c.position[2]] };
    });
  }, [holes, hasOrigin, lon0, lat0, groundY]);

  if (!cylinders.length) return null;
  return (
    <group name="boreholes">
      {cylinders.map(c => (
        <mesh key={c.key} position={c.position}>
          <cylinderGeometry args={[columnRadius, columnRadius, c.height, 12]} />
          <meshStandardMaterial color={c.color} />
        </mesh>
      ))}
    </group>
  );
}
